/**
 * The KDS entry point — the composition root.
 *
 * Master plan §4 M5, §3 K8, §3 K10.
 *
 * Like the POS's, this file decides which implementations get wired together
 * and holds no rules of its own. It is markedly simpler than the till's, and
 * the differences are all consequences of one fact: A KITCHEN SCREEN TAKES NO
 * MONEY.
 *
 * So there is no OPFS database, no signed config bundle, no PIN. A bump that is
 * lost in a power cut is a ticket that reappears on the board and gets bumped
 * again — annoying, and recoverable by looking at the pass. A sale lost the
 * same way is gone. The outbox is in memory for exactly that reason, and the
 * reason is worth stating rather than inheriting by accident.
 */

import { forgetPairing, pairingCodeFromHash, readPairing, type DevicePairing } from "@gf/sync-client/pairing";
import { scopeLocalData, takeOverLocalData, unsentEvents } from "@gf/sync-client/local-owner";
import { createApp, h, reactive, ref, shallowRef } from "vue";
import { Translator, negotiateLocale, type Catalog } from "@gf/kernel/i18n";
import { cssVariables, type DensityName, type ThemeName } from "@gf/design";
// The typeface and the shared layer. See the note in `apps/pos/src/main.ts`.
import "@gf/ui/fonts.css";
import "@gf/ui/base.css";
import {
  MemoryOutboxStore,
  Outbox,
  SqliteOutboxStore,
  createDrainLoop,
  createUploader,
} from "@gf/sync-client";
import { createOpfsDriver } from "@gf/sync-client/opfs";
import { StationSession, type BoardTicket } from "./station";
import { BoardFetchError, fetchBoard } from "./board-client";
import { fetchStationDishes, setSoldOut, type StationDish } from "./availability-client";

import { boardRoot } from "./board-root";
import { mayUpdateBoard } from "./update-rule";

// --- Theme -----------------------------------------------------------------

/**
 * KIOSK DENSITY, and dark by default.
 *
 * A wall-mounted screen is read at two metres by somebody holding a pan, and a
 * kitchen is bright. The POS's `pos` density is sized for a hand at arm's
 * length and is too small here; `kiosk` is the one the design tokens already
 * define for exactly this.
 */
function applyTheme(theme: ThemeName): void {
  const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const style = document.createElement("style");
  style.dataset["gfTokens"] = "";
  style.textContent = `:root {\n${cssVariables(theme, "kiosk" satisfies DensityName, reducedMotion)}\n}`;

  document.querySelector("style[data-gf-tokens]")?.remove();
  document.head.appendChild(style);
}

// --- Language --------------------------------------------------------------

const AVAILABLE = ["cs", "en", "uk", "sk"] as const;
const FALLBACK = "cs";

const load = async (locale: string): Promise<Catalog> => {
  const modules: Record<string, () => Promise<Record<string, unknown>>> = {
    cs: () => import("./locales/cs"),
    en: () => import("./locales/en"),
    uk: () => import("./locales/uk"),
    sk: () => import("./locales/sk"),
  };
  return (await modules[locale]!()) [locale] as Catalog;
};

/**
 * The DEPLOYMENT's language outranks the device's.
 *
 * A wall-mounted kitchen screen has no user to set a preference and frequently
 * reports whatever the browser image shipped with. `VITE_GF_LOCALE` is how a
 * Czech kitchen stays Czech and a Ukrainian brigade's screen stays Ukrainian —
 * a property of the venue, not of the tablet.
 */
/**
 * A COOK'S CHOICE OUTRANKS THE DEPLOYMENT'S, and is remembered on the screen.
 *
 * The pin is right for a wall-mounted board with no user. But a brigade
 * changes, and the person on the pass tonight may read Ukrainian far better
 * than Czech. Applied HERE rather than switched after mount, so the board does
 * not render in Czech and then flip in front of somebody plating.
 */
const LOCALE_KEY = "gf.kds.locale";
const pinned = localStorage.getItem(LOCALE_KEY) ?? import.meta.env["VITE_GF_LOCALE"];
const locale =
  typeof pinned === "string" && (AVAILABLE as readonly string[]).includes(pinned)
    ? (pinned as (typeof AVAILABLE)[number])
    : negotiateLocale([...navigator.languages], [...AVAILABLE], FALLBACK);
const catalogs = [await load(FALLBACK), ...(locale === FALLBACK ? [] : [await load(locale)])];

const build = (of: readonly Catalog[], at: string): Translator =>
  new Translator([...of], {
    locale: at,
    // Czech, not English. A Czech kitchen must never be shown an English word
    // because a key is missing.
    fallbackLocale: FALLBACK,
    onMissing: (key, missingIn) => console.warn(`i18n: missing "${key}" in ${missingIn}`),
  });

// --- Who this screen is ----------------------------------------------------------

/**
 * The build says where the API is; the venue, the screen's id, its key and its
 * STATION come from pairing — a code the office made for "Kuchyně", claimed
 * once and kept on the device. A development build that names them in its
 * environment (the demo, with a kitchen and a bar) is paired already.
 */
const pairingStore = ((): Storage | null => {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
})();
const apiBase = import.meta.env["VITE_GF_API"] ?? "http://localhost:3000";
/** A pairing link (`#pair=123456`) outranks the stored pairing; see the till's main.ts. */
const linkedCode = pairingCodeFromHash(location.hash);
if (linkedCode) history.replaceState(null, "", location.pathname + location.search);
// A link opened in a tab that is already running is only a fragment change.
addEventListener("hashchange", () => {
  if (pairingCodeFromHash(location.hash)) location.reload();
});
const paired: DevicePairing =
  (import.meta.env["VITE_GF_API_KEY"]
    ? {
        apiKey: import.meta.env["VITE_GF_API_KEY"],
        tenantId: import.meta.env["VITE_GF_TENANT"] ?? "11111111-1111-4111-8111-111111111111",
        venueId: import.meta.env["VITE_GF_VENUE"] ?? "33333333-3333-4333-8333-333333333333",
        deviceId: import.meta.env["VITE_GF_DEVICE"] ?? "55555555-5555-4555-8555-555555555555",
        deviceSecret: "unused-by-a-kitchen-screen",
        kind: "kitchen" as const,
        stationId: import.meta.env["VITE_GF_STATION"] ?? "kitchen",
        name: "development kitchen screen",
      }
    : null) ??
  (linkedCode ? null : readPairing(pairingStore, "kitchen")) ??
  (await (
    await import("./pairing-screen")
  ).pairThisDevice({
    baseUrl: apiBase,
    storage: pairingStore,
    kind: "kitchen",
    copy: "kds",
    copyFrom: { catalogs, more: async () => [], build, locale },
    linkedCode,
  }));

const stationId = paired.stationId ?? "kitchen";

// A screen paired as the PICKUP display shows guests their numbers, and is nothing else.
// Themed first: it returns before the board's own mount, and a screen without its
// tokens renders as bare text (the guest phone once shipped that way for three days).
if (stationId === "pickup") {
  applyTheme(window.matchMedia("(prefers-color-scheme: light)").matches ? "light" : "dark");
  await (await import("./pickup-display")).showPickupDisplay({
    baseUrl: apiBase,
    apiKey: paired.apiKey,
    venueId: paired.venueId,
    t: build(catalogs, locale),
  });
}
const config = {
  baseUrl: apiBase,
  apiKey: paired.apiKey,
  venueId: paired.venueId,
  deviceId: paired.deviceId,
  /** Which pass this screen is: from pairing, never a guess. */
  stationId,
  /** Who a bump is attributed to: the station, since nobody signs in to a pass. */
  cookId: import.meta.env["VITE_GF_COOK"] ?? `station:${stationId}`,
} as const;

/**
 * Held in refs, because Vue evaluates root props ONCE, eagerly.
 *
 * This app has already shipped that bug: the board read a snapshot of its
 * props at mount and the kitchen screen never displayed a ticket in its life.
 * A plain variable here would be the same mistake in a new place.
 */
/**
 * `shallowRef`, NOT `ref`.
 *
 * `ref` on an object calls `reactive` on it, which returns a Proxy, and
 * `Translator` keeps its catalogues in a `#private` field — a private-field
 * access through a Proxy fails the brand check, so every `t.t(...)` throws
 * "Receiver must be an instance of class anonymous" and the board renders
 * nothing. Found on the till, fixed in both places at once.
 */
const translatorRef = shallowRef(build(catalogs, locale));
const localeRef = ref<string>(locale);
const held = new Map<string, Catalog>([[FALLBACK, catalogs[0]!], [locale, catalogs.at(-1)!]]);

async function setLocale(code: string): Promise<void> {
  if (code === localeRef.value || !(AVAILABLE as readonly string[]).includes(code)) return;

  try {
    let catalogue = held.get(code);
    if (!catalogue) {
      catalogue = await load(code);
      held.set(code, catalogue);
    }
    const fallback = held.get(FALLBACK);
    // The fallback FIRST, which is the order `Translator` resolves in.
    translatorRef.value = build(
      code === FALLBACK || !fallback ? [catalogue] : [fallback, catalogue],
      code,
    );
    localeRef.value = code;
    document.documentElement.lang = code;
    localStorage.setItem(LOCALE_KEY, code);
  } catch (error) {
    // A chunk that will not load leaves the board in the language it is in.
    // Half a translated screen over a hot pass is worse than the wrong one.
    console.warn("i18n: could not switch language —", (error as Error).message);
  }
}

/**
 * The offline shell.
 *
 * A wall-mounted board is reloaded by a power blip, a browser update or
 * somebody's elbow, and before this existed that reload showed Chrome's
 * dinosaur — on a product whose differentiator is that a venue keeps working
 * when the wifi does not.
 *
 * A new build waits for a quiet board (`update-rule.ts`) and the screen then
 * reloads into it. Left to the browser it would never apply: a waiting worker
 * activates only when every tab is closed, and a wall screen's never is.
 */
async function registerServiceWorker(): Promise<void> {
  if (!("serviceWorker" in navigator)) return;

  /**
   * NOT IN DEVELOPMENT, because there is nothing to register: `vite dev` does
   * not build `src/sw.ts`, so `/sw.js` falls through to the HTML fallback and
   * the browser refuses it as `text/html`. Red text in the devtools of every
   * demo is how people learn to ignore the console.
   */
  if (import.meta.env["DEV"]) return;

  let registration: ServiceWorkerRegistration;
  try {
    registration = await navigator.serviceWorker.register("/sw.js", { type: "module" });
  } catch (error) {
    // Not fatal — the board still works while the page is open. It is a defect
    // in a built artifact all the same: this screen can no longer cold-start
    // without a network, which is the one thing the file exists for.
    console.warn(
      "kds: no service worker — this build cannot cold-start offline.",
      error instanceof Error ? error.message : error,
    );
    return;
  }

  let touchedAt = Date.now();
  window.addEventListener("pointerdown", () => {
    touchedAt = Date.now();
  });
  const { followAppUpdates } = await import("@gf/sync-client/app-update");
  followAppUpdates({
    container: navigator.serviceWorker,
    registration,
    mayApply: () => mayUpdateBoard({ tickets: state.tickets.length, queued: state.queued, idleMs: Date.now() - touchedAt }),
    reload: () => location.reload(),
  });
}

// --- The station -----------------------------------------------------------

/**
 * DURABLE, because a lost bump is a dish cooked twice.
 *
 * This was `MemoryOutboxStore`. Driven through an outage for the first time:
 * the board kept its tickets, a cook acknowledged the allergens and bumped the
 * ticket with no network — all correct — and then the screen was reloaded.
 * The queued bump was in memory and went with the page. When the network came
 * back the ticket REAPPEARED on the pass, and the kitchen made it again.
 *
 * A wall-mounted screen is reloaded by a power blip, a browser update or
 * somebody's elbow, and it is the one surface where the cost of forgetting is
 * a second plate of food rather than a retry.
 *
 * IN-MEMORY IS STILL THE FALLBACK, deliberately, exactly as on the till. If
 * OPFS is unavailable a kitchen screen that refused to start would be a pass
 * with no tickets on it, which is worse than one that forgets on reload — so
 * it degrades and the console says which.
 */
const outboxStore = await (async () => {
  try {
    const driver = createOpfsDriver();
    const store = new SqliteOutboxStore(driver);
    await store.migrate();
    // The local database belongs to this pairing; see the till's main.ts.
    const owned = await scopeLocalData(driver, paired);
    if (owned.kind === "WIPED") {
      location.reload();
      await new Promise<never>(() => {});
    }
    if (owned.kind === "BLOCKED") {
      const t = build(catalogs, locale);
      await (await import("@gf/ui/unsent-screen")).showUnsentBlock({
        words: {
          title: t.t("kds.unsent.title"),
          lead: t.t("kds.unsent.lead", { count: owned.unsent }),
          downloadLabel: t.t("kds.unsent.download"),
          discardLabel: t.t("kds.unsent.discard"),
          confirmLead: t.t("kds.unsent.confirmLead"),
          confirmLabel: t.t("kds.unsent.confirm"),
          cancelLabel: t.t("kds.unsent.cancel"),
        },
        exportJson: async () => JSON.stringify({ previous: owned.previous, events: await unsentEvents(driver) }, null, 2),
        fileName: `gastrofirst-unsent-${owned.previous.deviceId}.json`,
        discard: () => takeOverLocalData(driver, paired),
      });
    }
    return store;
  } catch (error) {
    console.error("kds: OPFS is unavailable, falling back to memory —", error);
    return new MemoryOutboxStore();
  }
})();

const outbox = new Outbox(outboxStore, { batchSize: 50, maxAttempts: 3 });

let hlcSeq = 0;

const station = new StationSession({
  outbox,
  stationId: config.stationId,
  cookId: config.cookId,
  now: () => new Date(),
  newId: () => crypto.randomUUID(),
  nextHlc: () => `${String(++hlcSeq).padStart(15, "0")}:000000:${config.deviceId}`,
});

const DETAILED_KEY = "gf.kds.detailed";

const state = reactive<{
  tickets: readonly BoardTicket[];
  online: boolean;
  queued: number;
  recallable: BoardTicket | null;
  /** Per-dish Start / Hotovo: this screen's choice, remembered on it. */
  detailed: boolean;
  /** This station's dishes; null until the menu has answered once. */
  dishes: readonly StationDish[] | null;
  /** A training venue: practice orders, said in the bar. */
  training: boolean;
  /** The venue does not have the kitchen display module. */
  disabled: boolean;
}>({
  tickets: [],
  online: navigator.onLine,
  queued: 0,
  recallable: null,
  detailed: (() => {
    try {
      return localStorage.getItem(DETAILED_KEY) === "1";
    } catch {
      return false;
    }
  })(),
  dishes: null,
  training: false,
  disabled: false,
});

const wire = () => ({ baseUrl: config.baseUrl, apiKey: config.apiKey, venueId: config.venueId });

/** Whether this is the training venue, from its config. A courtesy: the board works without it. */
async function loadTraining(): Promise<void> {
  try {
    const response = await fetch(`${config.baseUrl}/v1/config?venueId=${encodeURIComponent(config.venueId)}`, {
      headers: { authorization: `Bearer ${config.apiKey}` },
    });
    if (!response.ok) return;
    const signed = (await response.json()) as { bundle?: { payload?: { training?: unknown; modules?: unknown } } };
    state.training = signed.bundle?.payload?.training === true;
    // Only a bundle that names its modules can say the screen is off; one from
    // before modules existed had a kitchen screen.
    const modules = signed.bundle?.payload?.modules;
    state.disabled = Array.isArray(modules) && !modules.includes("kitchen-display");
  } catch {
    // Offline at start: the badge comes with the next start.
  }
}

/** What this station cooks and what has run out. A failure keeps what is shown. */
async function loadDishes(): Promise<void> {
  try {
    state.dishes = await fetchStationDishes({ ...wire(), stationId: config.stationId });
  } catch (error) {
    console.warn("kds: keeping the dishes on screen —", (error as Error).message);
  }
}

const refresh = async (): Promise<void> => {
  state.tickets = station.tickets();
  state.recallable = station.recallable();
  state.queued = await outbox.depth();
};

// The undo closes on its own when its window does, not at the next poll.
setInterval(() => {
  if (state.recallable && !station.recallable()) state.recallable = null;
}, 1_000);

for (const event of ["online", "offline"] as const) {
  window.addEventListener(event, () => {
    state.online = navigator.onLine;
  });
}

// --- Mount -----------------------------------------------------------------

applyTheme(window.matchMedia("(prefers-color-scheme: light)").matches ? "light" : "dark");

/**
 * THE PROPS, AS A FUNCTION — the third app to need this fix.
 *
 * `createApp(StationBoard, {...})` passes root props ONCE. Vue copies them into
 * the component's internal props object at mount and never looks at the source
 * again, so getters over reactive state are evaluated exactly once: at mount,
 * when the first board fetch has not returned. Nothing re-renders, ever.
 *
 * THE KITCHEN SCREEN HAD THEREFORE NEVER DISPLAYED A TICKET. The board arrived,
 * the session held it, `station.tickets()` returned two — and the screen said
 * "Hotovo. Žádné objednávky." The render gate asserted exactly that string and
 * passed, because an empty board legitimately says it.
 *
 * The till and the back office were both fixed this way weeks and hours ago
 * respectively; this one was missed because nothing had ever put a ticket in
 * front of it and looked.
 */
const boardProps = () => ({
  get tickets() {
    return state.tickets;
  },
  /**
   * THE STATION'S NAME, not its identifier.
   *
   * The header read `config.stationId` and so said "KITCHEN" in capitals on a
   * Czech kitchen screen — a database key, two metres up a wall, in front of
   * cooks. The seeded venue has carried "Kuchyně" and "Bar" all along; nothing
   * read them.
   *
   * Translated rather than taken from the server, because the name of a station
   * is the same fact in four languages and a Ukrainian cook's screen should say
   * "Кухня". An unknown station falls back to its identifier, which is ugly and
   * true — better than a blank header on a screen nobody can fix mid-service.
   */
  stationName: (() => {
    const key = `kds.station.${config.stationId}`;
    const name = translatorRef.value.t(key);
    // `t` returns the KEY when it has no message, which is the documented
    // behaviour and exactly the signal needed here: a station nobody has named
    // falls back to its identifier. Ugly and true beats a blank header two
    // metres up a wall in the middle of service.
    return name === key ? config.stationId : name;
  })(),
  // Read through the ref, so switching language re-renders. A captured value
  // here is the bug this app has already shipped once.
  t: translatorRef.value,
  locale: localeRef.value,
  locales: AVAILABLE,
  onLocale: (code: string) => {
    void setLocale(code);
  },
  get online() {
    return state.online;
  },
  get queued() {
    return state.queued;
  },
  get unacknowledged() {
    // Computed every render from the board, never stored. A second record of
    // which lines are outstanding disagrees with the first the moment one is
    // acknowledged.
    return Object.fromEntries(
      state.tickets.map((t) => [
        t.ticketId,
        station.unacknowledged(t.ticketId).map((i) => i.selectionId),
      ]),
    );
  },

  onAcknowledge: ({ ticketId, selectionId }: { ticketId: string; selectionId: string }) => {
    void (async () => {
      await station.acknowledge(ticketId, selectionId);
      await refresh();
    })();
  },
  onStart: ({ ticketId, selectionId }: { ticketId: string; selectionId: string }) => {
    void (async () => {
      await station.start(ticketId, selectionId);
      await refresh();
    })();
  },
  onFinish: ({ ticketId, selectionId }: { ticketId: string; selectionId: string }) => {
    void (async () => {
      await station.finish(ticketId, selectionId);
      await refresh();
    })();
  },
  onBump: (ticketId: string) => {
    void (async () => {
      await station.bump(ticketId);
      await refresh();
    })();
  },
  get recallable() {
    return state.recallable;
  },
  get detailed() {
    return state.detailed;
  },
  onDetailed: (on: boolean) => {
    state.detailed = on;
    try {
      localStorage.setItem(DETAILED_KEY, on ? "1" : "0");
    } catch {
      // Remembered for this session only.
    }
  },
  get training() {
    return state.training;
  },
  get disabled() {
    return state.disabled;
  },
  get menuItems() {
    return state.dishes ?? undefined;
  },
  onToggleSoldOut: (itemRef: string, soldOut: boolean) => {
    // Shown at once; put back if the server refuses, so the screen never
    // claims a dish is off that the tills are still selling.
    const before = state.dishes;
    state.dishes = (before ?? []).map((d) => (d.itemRef === itemRef ? { ...d, soldOut } : d));
    void setSoldOut({ ...wire(), itemRef, soldOut })
      .then(loadDishes)
      .catch((error: Error) => {
        console.warn("kds: could not change what has run out —", error.message);
        state.dishes = before;
      });
  },
  onRecall: (ticketId: string) => {
    void (async () => {
      await station.recall(ticketId);
      await refresh();
    })();
  },
});

// Mounted LAST and through a render function, so every prop is re-read on every
// render rather than captured once.
const vueApp = createApp(boardRoot(boardProps));
// Vue keeps a render error to itself; handed on, the error report hears it.
vueApp.config.errorHandler = (error) => {
  console.error(error);
  dispatchEvent(new ErrorEvent("error", { error }));
};
vueApp.mount("#app");
void import("@gf/sync-client/error-report").then(({ reportErrors, postReports }) =>
  reportErrors({
    target: window,
    where: () => location.pathname,
    send: postReports({ baseUrl: config.baseUrl, app: "kds", apiKey: () => config.apiKey }),
  }),
);

// --- Polling ---------------------------------------------------------------

/**
 * Every three seconds.
 *
 * A kitchen wants a ticket the moment it is fired, and three seconds is the
 * longest a cook should wait to learn that a table ordered. This is a poll
 * rather than a subscription because a poll survives a flaky venue LAN without
 * a reconnect state machine, and the board is small.
 *
 * A FAILURE KEEPS WHAT THE SCREEN HAS. Food fired before an outage still has to
 * be cooked, and a screen that blanks itself is worse than one a minute stale.
 */
/** Tickets this screen has seen; a new one rings, but not the board's first load. */
let seenTickets: Set<string> | null = null;

async function syncBoard(): Promise<void> {
  try {
    const board = await fetchBoard({
      baseUrl: config.baseUrl,
      apiKey: config.apiKey,
      venueId: config.venueId,
      stationId: config.stationId,
    });
    station.install(board);
    const ids = new Set(board.map((t) => t.ticketId));
    if (seenTickets && [...ids].some((id) => !seenTickets!.has(id))) {
      void import("@gf/ui/chime").then(({ chime }) => chime());
    }
    seenTickets = ids;
    state.disabled = false;
  } catch (error) {
    if (error instanceof BoardFetchError && error.status === 402) state.disabled = true;
    console.warn("board: keeping what is on screen —", (error as Error).message);
  }
  await refresh();
}

const upload = createUploader({
  baseUrl: config.baseUrl,
  apiKey: config.apiKey,
  venueId: config.venueId,
  deviceId: config.deviceId,
});

/**
 * One batch per tick, with backoff — the SAME loop the till runs.
 *
 * This was twelve lines of its own here, copied from the till before the till
 * fixed them, and the difference was a bug: the `online` handler below reset a
 * backoff variable and nothing else, which does nothing at all to a timer that
 * has already been scheduled. A cook bumped a ticket during an outage, the wifi
 * came back, and the server did not hear about it for up to another thirty
 * seconds — long enough for the same food to be cooked twice.
 *
 * See `@gf/sync-client`'s `drain-loop` for the rest of the reasoning.
 */
const drainLoop = createDrainLoop({
  drain: () => outbox.drain(upload),
  afterDrain: refresh,
  isOnline: () => navigator.onLine,
  setTimeout: (fn, ms) => window.setTimeout(fn, ms),
  clearTimeout: (id) => window.clearTimeout(id),
});

void refresh();
void syncBoard();
void loadDishes();
void loadTraining();
// Another screen, the office or a till may mark a dish too; nothing on the
// event stream says so, so this asks.
window.setInterval(() => void loadDishes(), 30_000);
void registerServiceWorker();
// Storage the browser may not clear when the disk fills: a queued bump is a dish not cooked twice.
void navigator.storage?.persist?.().catch(() => false);
drainLoop.start();

/**
 * THE MOMENT A TICKET MOVES, not at the next poll: the venue's stream says so
 * and the board is fetched at once — a round fired on a phone is on the pass
 * in about a second. The poll stays underneath, slower, for when the stream
 * cannot get through; an announcement is a hint, never the only way in.
 */
const KITCHEN_TYPES = /^(ticket|item|allergens)\.|^selection\.voided$/;
let queued: number | null = null;
const syncSoon = (): void => {
  queued ??= window.setTimeout(() => {
    queued = null;
    void syncBoard();
  }, 150);
};
void import("@gf/sync-client/venue-stream").then(({ followVenue }) =>
  followVenue({
    baseUrl: config.baseUrl,
    apiKey: config.apiKey,
    venueId: config.venueId,
    onOpen: syncSoon,
    onChange: (change) => {
      if (change.types.some((t) => KITCHEN_TYPES.test(t))) syncSoon();
      // A module switched on or off in the office reaches the screen at once.
      if (change.types.includes("config.published")) void loadTraining().then(syncSoon);
    },
    // Unpaired in the office: back to the code screen. Never for a development build.
    onRefused: () => {
      if (import.meta.env["VITE_GF_API_KEY"]) return;
      forgetPairing(pairingStore, "kitchen");
      location.reload();
    },
  }),
);
window.setInterval(() => void syncBoard(), 10_000);
window.addEventListener("online", () => {
  drainLoop.reconnected();
  void syncBoard();
});
